import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "clement.admin.token";

export default function AdminWaitlist() {
  const [token, setToken] = useState(
    () => localStorage.getItem(TOKEN_KEY) || "",
  );
  const [authed, setAuthed] = useState(Boolean(token));
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!authed || !token) return;
    let cancel = false;
    setLoading(true);
    setError("");
    axios
      .get(`${API}/admin/waitlist`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        if (!cancel) setEntries(res.data);
      })
      .catch((err) => {
        if (cancel) return;
        const status = err?.response?.status;
        if (status === 401 || status === 403) {
          setError("Invalid token. Please sign in again.");
          localStorage.removeItem(TOKEN_KEY);
          setAuthed(false);
        } else {
          setError("Could not load registry submissions.");
        }
      })
      .finally(() => !cancel && setLoading(false));
    return () => {
      cancel = true;
    };
  }, [authed, token]);

  const onSignIn = (e) => {
    e.preventDefault();
    const t = token.trim();
    if (!t) return;
    localStorage.setItem(TOKEN_KEY, t);
    setAuthed(true);
  };

  const onSignOut = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
    setAuthed(false);
    setEntries([]);
  };

  const downloadCsv = async () => {
    try {
      const res = await axios.get(`${API}/admin/waitlist.csv`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = "clement-registry.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      setError("CSV export failed.");
    }
  };

  if (!authed) {
    return (
      <main
        data-testid="admin-page"
        className="min-h-screen bg-[#0C0F0A] text-[#F5F2EB] flex items-center justify-center px-6 relative overflow-hidden"
      >
        <div aria-hidden className="absolute inset-0 pointer-events-none">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_60%_50%_at_50%_20%,rgba(197,160,89,0.10),transparent_65%)]" />
          <div className="absolute inset-y-0 left-1/2 -translate-x-1/2 w-px bg-gradient-to-b from-transparent via-[rgba(197,160,89,0.18)] to-transparent" />
        </div>

        <motion.form
          onSubmit={onSignIn}
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          className="relative w-full max-w-md text-center"
          data-testid="admin-signin-form"
        >
          <div className="flex items-center justify-center gap-4">
            <span className="block w-12 h-px bg-[#C5A059]" />
            <span className="text-[10px] uppercase tracking-[0.42em] text-[#C5A059]">
              The House
            </span>
            <span className="block w-12 h-px bg-[#C5A059]" />
          </div>
          <h1 className="mt-8 font-serif-display text-3xl sm:text-4xl text-[#F5F2EB] font-light leading-tight">
            Registry Console
          </h1>
          <p className="mt-4 text-[#A8A9AD] text-sm">
            Private access. Enter the administration key to continue.
          </p>

          <div className="mt-12 text-left">
            <label className="block mb-4 text-[10px] uppercase tracking-[0.34em] text-[#C5A059]/85">
              Administration Key
            </label>
            <input
              type="password"
              autoFocus
              autoComplete="off"
              value={token}
              onChange={(e) => setToken(e.target.value)}
              data-testid="admin-token-input"
              className="w-full bg-transparent border-0 border-b border-[rgba(245,242,235,0.18)] px-0 py-3 text-[#F5F2EB] focus:outline-none focus:border-[#C5A059] text-[15px] tracking-wider"
            />
          </div>

          {error && (
            <p className="mt-6 text-[#e07a5f] text-sm" data-testid="admin-error">
              {error}
            </p>
          )}

          <div className="mt-10 flex flex-col items-center">
            <button
              type="submit"
              data-testid="admin-signin-submit"
              className="group inline-flex items-center justify-center gap-5 border border-[#C5A059] text-[#C5A059] px-12 py-4 text-[11px] uppercase tracking-[0.42em] hover:bg-[#C5A059] hover:text-[#0C0F0A] transition-colors duration-500"
            >
              Enter Console
              <span
                aria-hidden
                className="inline-block w-7 h-px bg-current transition-all duration-500 group-hover:w-10"
              />
            </button>
          </div>
        </motion.form>
      </main>
    );
  }

  return (
    <main
      data-testid="admin-page"
      className="min-h-screen bg-[#0C0F0A] text-[#F5F2EB] relative overflow-hidden"
    >
      <div aria-hidden className="absolute inset-0 pointer-events-none">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_75%_45%_at_50%_-10%,rgba(197,160,89,0.08),transparent_70%)]" />
      </div>

      <header className="relative border-b border-[rgba(197,160,89,0.18)]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6">
          <div>
            <div className="flex items-center gap-3">
              <span className="block w-8 h-px bg-[#C5A059]" />
              <span className="text-[10px] uppercase tracking-[0.42em] text-[#C5A059]">
                The House · Registry Console
              </span>
            </div>
            <h1 className="mt-3 font-serif-display text-3xl sm:text-4xl text-[#F5F2EB] font-light">
              Private Registry — Submissions
            </h1>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <span
              className="text-[10px] uppercase tracking-[0.32em] text-[#A8A9AD]"
              data-testid="admin-count"
            >
              {entries.length} entries
            </span>
            <button
              type="button"
              onClick={downloadCsv}
              data-testid="admin-export-csv"
              className="border border-[#C5A059] text-[#C5A059] px-6 py-3 text-[10px] uppercase tracking-[0.34em] hover:bg-[#C5A059] hover:text-[#0C0F0A] transition-colors"
            >
              Export CSV
            </button>
            <button
              type="button"
              onClick={onSignOut}
              data-testid="admin-signout"
              className="border border-[rgba(245,242,235,0.2)] text-[#A8A9AD] px-6 py-3 text-[10px] uppercase tracking-[0.34em] hover:border-[#C5A059] hover:text-[#C5A059] transition-colors"
            >
              Sign Out
            </button>
          </div>
        </div>
      </header>

      <section className="relative max-w-7xl mx-auto px-6 lg:px-10 py-14">
        {loading && (
          <p className="text-[#A8A9AD] text-sm uppercase tracking-[0.32em]">
            Loading registry…
          </p>
        )}

        {!loading && entries.length === 0 && (
          <div className="py-20 text-center">
            <p className="font-serif-display text-2xl text-[#F5F2EB] font-light">
              No submissions yet.
            </p>
            <p className="mt-3 text-[#A8A9AD] text-sm">
              Members of the registry will appear here as they request access.
            </p>
          </div>
        )}

        {!loading && entries.length > 0 && (
          <div className="space-y-px bg-[rgba(197,160,89,0.12)] border border-[rgba(197,160,89,0.18)]">
            {entries.map((e) => (
              <EntryCard key={e.id} entry={e} />
            ))}
          </div>
        )}

        {error && (
          <p className="mt-8 text-[#e07a5f] text-sm" data-testid="admin-error">
            {error}
          </p>
        )}
      </section>
    </main>
  );
}

function EntryCard({ entry: e }) {
  const ts = e.created_at ? new Date(e.created_at) : null;
  const tsString = ts
    ? ts.toLocaleString(undefined, {
        year: "numeric",
        month: "short",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "—";

  return (
    <article
      data-testid={`admin-entry-${e.id}`}
      className="bg-[#0C0F0A] px-6 lg:px-10 py-8 grid grid-cols-1 lg:grid-cols-12 gap-x-10 gap-y-6"
    >
      <div className="lg:col-span-3">
        <div className="text-[10px] uppercase tracking-[0.32em] text-[#A8A9AD]">
          {tsString}
        </div>
        <div className="mt-3 font-serif-display text-[#F5F2EB] text-xl sm:text-2xl font-light leading-tight">
          {e.first_name} {e.last_name || ""}
        </div>
        <a
          href={`mailto:${e.email}`}
          className="mt-2 inline-block text-[#C5A059] text-sm tracking-wide hover:text-[#F5F2EB]"
        >
          {e.email}
        </a>
      </div>

      <div className="lg:col-span-3 grid grid-cols-2 gap-x-6 gap-y-5 text-sm">
        <Cell label="Collection" value={e.preferred_collection || "—"} accent />
        <Cell label="Country" value={e.country || "—"} />
        <Cell label="Home Club" value={e.home_club || "—"} fullSpan />
      </div>

      <div className="lg:col-span-6">
        <div className="text-[10px] uppercase tracking-[0.34em] text-[#C5A059]/85 mb-2">
          Personal Notes
        </div>
        <p className="text-[#d6d3c6] leading-relaxed text-[14px] whitespace-pre-wrap">
          {e.personal_notes ? e.personal_notes : <span className="text-[#A8A9AD]/60 italic">—</span>}
        </p>
      </div>
    </article>
  );
}

function Cell({ label, value, accent = false, fullSpan = false }) {
  return (
    <div className={fullSpan ? "col-span-2" : ""}>
      <div className="text-[10px] uppercase tracking-[0.34em] text-[#C5A059]/85 mb-1.5">
        {label}
      </div>
      <div className={`text-[14px] ${accent ? "text-[#F5F2EB]" : "text-[#d6d3c6]"}`}>
        {value}
      </div>
    </div>
  );
}
